import { ExclamationCircleFilled } from '@ant-design/icons'
import type { Carer } from '@lmc/ts-common'
import { Chevron, Tag, ThreeDots } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Dropdown } from 'antd'
import { ListItem } from 'components/generic'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { carePlansConstants } from 'locale/carePlans'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import type { PopulatedResidentNeed } from '../../../../shared/hooks/useCarePlanPopulated'
import AiIcon from './AiIcon'

/** Figma accent for the AI-assisted tag; no ui-kit theme equivalent. */
const AI_TAG_PURPLE = '#562F7E'

interface NeedItemProps {
  need: PopulatedResidentNeed
  disabled?: boolean
  lastReviewDate?: string
  onRemove?: (needId: string) => void
  /** Marks a need that originated from an accepted AI suggestion. */
  aiAssisted?: boolean
  testId?: string
}

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-'
  return dayjs(dateString).format('DD-MM-YYYY')
}

export const NeedsIcon = ({ size = 24 }: { size?: number }) => {
  const clipId = React.useId()
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          fill="#FF5887"
          d="M7.354 9.962 3.309 5.918a10.628 10.628 0 0 0 0 12.159l4.045-4.045a5.096 5.096 0 0 1 0-4.07ZM16.645 14.032l4.045 4.045a10.628 10.628 0 0 0 0-12.159l-4.045 4.044a5.095 5.095 0 0 1 0 4.07ZM14.035 7.353 18.08 3.31a10.628 10.628 0 0 0-12.16 0l4.045 4.044a5.095 5.095 0 0 1 4.07 0ZM9.964 16.645 5.92 20.689a10.628 10.628 0 0 0 12.159 0l-4.045-4.044a5.095 5.095 0 0 1-4.07 0Z"
        />
        <path
          fill="#FFCAD9"
          d="M15.59 8.41c.46.46.81.99 1.056 1.554L20.69 5.92c-.35-.5-.738-.981-1.184-1.427a10.656 10.656 0 0 0-1.427-1.184l-4.044 4.044A5.055 5.055 0 0 1 15.59 8.41ZM20.69 18.08l-4.044-4.045a5.056 5.056 0 0 1-1.056 1.555c-.461.461-.99.81-1.555 1.056l4.044 4.044c.5-.35.98-.738 1.427-1.183.446-.446.834-.927 1.184-1.427ZM8.41 15.59a5.034 5.034 0 0 1-1.056-1.555L3.31 18.08c.35.5.738.98 1.184 1.426.446.446.927.834 1.426 1.184l4.045-4.044A5.056 5.056 0 0 1 8.41 15.59ZM8.41 8.41c.461-.462.99-.811 1.555-1.057L5.921 3.309c-.5.35-.981.738-1.427 1.183-.446.446-.834.927-1.184 1.427l4.045 4.045A5.055 5.055 0 0 1 8.41 8.409Z"
        />
      </g>
      <defs>
        <clipPath id={clipId}>
          <path fill="#fff" d="M0 0h24v24H0z" />
        </clipPath>
      </defs>
    </svg>
  )
}

const NeedItem: React.FC<NeedItemProps> = ({
  need,
  disabled = false,
  lastReviewDate,
  onRemove,
  aiAssisted = false,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.CARE_PLANS)
  const carers: Carer[] = useSelector((state: any) => state.data.carers)
  const [showNotes, setShowNotes] = useState(false)

  const resolveCarerName = (value?: string): string => {
    if (!value) return ''
    const carer = carers?.find((c) => c.id === value)
    if (carer) return `${carer.name.first} ${carer.name.last}`
    return value
  }

  const isUpdated =
    lastReviewDate &&
    need.editedOn &&
    dayjs(need.editedOn).isAfter(dayjs(lastReviewDate))

  const metadataParts: string[] = []
  if (need.addedOn) {
    const name = resolveCarerName(need.addedBy)
    metadataParts.push(
      t(carePlansConstants.NEED_CREATED_BY, {
        date: formatDate(need.addedOn),
        name: name || '-'
      })
    )
  }
  if (need.editedOn) {
    const name = resolveCarerName(need.editedBy)
    metadataParts.push(
      t(carePlansConstants.NEED_UPDATED_BY, {
        date: formatDate(need.editedOn),
        name: name || '-'
      })
    )
  }
  const subLabel = metadataParts.join(' \u2022 ')

  return (
    <NeedItemWrapper>
      <ListItem
        hasBorder
        borderRadius={20}
        style={{ padding: '8px 16px' }}
        renderImage={() => (
          <IconBox>
            <NeedsIcon size={32} />
          </IconBox>
        )}
        label={need.need.name}
        subLabel={subLabel}
        multiSelect
        dataTestId={testId || 'need-item'}
        renderBody={
          need.notes || isUpdated
            ? () => (
                <NotesBody>
                  {isUpdated && (
                    <WarningText>
                      <ExclamationCircleFilled />{' '}
                      {t(carePlansConstants.NEED_UPDATED_WARNING)}
                    </WarningText>
                  )}
                  {need.notes && (
                    <>
                      <NotesToggle
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation()
                          setShowNotes((prev) => !prev)
                        }}
                      >
                        {showNotes
                          ? t(carePlansConstants.NEED_HIDE_NOTES)
                          : t(carePlansConstants.NEED_SHOW_NOTES)}
                        <Chevron direction={showNotes ? 'up' : 'down'} />
                      </NotesToggle>
                      {showNotes && <NotesContent>{need.notes}</NotesContent>}
                    </>
                  )}
                </NotesBody>
              )
            : undefined
        }
      >
        <RightSection>
          {aiAssisted && (
            <AiTag
              color="purple"
              icon={<AiIcon size={16} color={AI_TAG_PURPLE} />}
            >
              {t(carePlansConstants.SUGGESTED_NEED_AI_ASSISTED)}
            </AiTag>
          )}
          {!disabled && onRemove && (
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'remove',
                    danger: true,
                    label: t(carePlansConstants.NEED_REMOVE_ACTION),
                    onClick: () => onRemove(need.id)
                  }
                ],
                style: { borderRadius: 20 }
              }}
              trigger={['click']}
              placement="bottomRight"
            >
              <StyledDotsButton
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <ThreeDots horizontal />
              </StyledDotsButton>
            </Dropdown>
          )}
        </RightSection>
      </ListItem>
    </NeedItemWrapper>
  )
}

const NeedItemWrapper = styled.div`
  .lmc-list-item-link-container {
    cursor: default !important;
  }

  .lmc-list-item:hover
    > .lmc-list-item-header
    > .lmc-list-item-link-container
    > .lmc-list-item-parent-link
    > .lmc-list-item-link {
    color: inherit !important;
  }
`

const WarningText = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #FFA500;
  margin-bottom: 4px;
`

const IconBox = styled.div`
  width: 40px;
  height: 40px;
  background: #FAFAFA;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const AiTag = styled(Tag)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin: 0;
`

const StyledDotsButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 4px;
`

const NotesBody = styled.div`
  padding: 4px 0 0 50px;
`

const NotesToggle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.status.primary};
  user-select: none;

  &:hover {
    color: ${({ theme }) => theme.color.hover?.primary || theme.color.status.primary};
  }

  svg {
    width: 12px;
    height: 12px;
    stroke-width: 2;
  }
`

const NotesContent = styled(Text)`
  display: block;
  margin-top: 4px;
  color: ${({ theme }) => theme.color.neutral.color3};
  white-space: pre-wrap;
  font-size: 14px;
`

export default NeedItem
